import { FunctionComponent, useEffect, useState } from 'react'
import { useTasksHandlers } from '../../../../services/actions/tasks/useTasksHandlers'
import Text from '../../../../components/Text'
import moment from 'moment'
import styled from 'styled-components'
import { styles } from '../../../../styles/themes/style'
import SvgFragment from '../../../../fragments/SvgFragment'
import Tag from '../../../../components/Tag'
import { parseLinksInText } from '../../../../utils'
import { useAppHandlers } from '../../../../services/actions/app/useAppHandlers'
import Row from '../../../../components/Row'
import Column from '../../../../components/Column'
import LoaderCell from '../../../../components/LoaderCell'
import { ITaskResponse } from '../../../../interfaces/response/task'
import { SidebarTypes } from '../../../../interfaces/store/appStore'

interface ITasksTableProps {
    tasks: ITaskResponse[]
    tasksLoading: boolean
}

interface ICheckMark {
    $completed?: boolean
}

interface IListRowProps {
    $isLast?: boolean
}

const CHECK_ICON = (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M7.16321 11.2024L12.5299 6.06189C12.7121 5.88731 12.7121 5.60429 12.5299 5.42973L11.8699 4.79757C11.6877 4.62302 11.3922 4.62302 11.21 4.79757L6.83324 8.98985L4.78985 7.03257C4.60761 6.858 4.31212 6.858 4.12988 7.03257L3.46993 7.66473C3.28769 7.8393 3.28769 8.12233 3.46993 8.29689L6.50326 11.2024C6.68548 11.377 6.98096 11.377 7.16321 11.2024Z"
            fill="white"
        />
    </svg>
)

export const renderTags = (props: number) => {
    switch (props) {
        case 0:
            return null
        case 1:
            return <Tag title="Low" $color={styles.colors.blue600} />
        case 2:
            return <Tag title="Medium" $color={styles.colors.green500} />;
        case 3:
            return <Tag title="High" $color={styles.colors.orange600} />;
        case 4:
            return <Tag title="Urgent" $color={styles.colors.red600} />;
        default:
            return null;
    }
}

const TasksTable: FunctionComponent<ITasksTableProps> = ({ tasks, tasksLoading }) => {
    const [currentTasks, setCurrentTasks ] = useState<ITaskResponse[]>(tasks)

    const { completeSingleTask } = useTasksHandlers()
    const {  openSidebar } = useAppHandlers()

    useEffect(() => {
        setCurrentTasks(tasks)
    }, [tasks])

    const onCompeteTaskHandler = async (taskId: string) => {
        const result = await completeSingleTask(taskId)
        if(result){
            setCurrentTasks(currentTasks.filter((task) => task._id !== result._id))
        }
    }

    const showList = currentTasks.length > 0

    return (
        <ListScroll>
            {!currentTasks?.length && tasksLoading ? (
                Array(5)
                    .fill(undefined)
                    .map((_, index) => (
                        <TaskSkeletonRow key={index} $isLast={index === 4}>
                            <LoaderCell $circle $width="16px" $height="16px" />
                            <SkeletonTaskTitle>
                                <LoaderCell $width="100%" $height="14px" />
                            </SkeletonTaskTitle>
                            <SkeletonRightMeta>
                                <LoaderCell $width="108px" $height="14px" />
                                <LoaderCell $width="62px" $height="20px" />
                            </SkeletonRightMeta>
                        </TaskSkeletonRow>
                    ))
            ) : showList ? (
                <TaskList>
                    {currentTasks.map((task, index) => (
                        <TaskGroup key={task._id} $isLast={index === currentTasks.length - 1}>
                            <ListRow
                                alignItems
                                onClick={() => openSidebar(SidebarTypes.TASK_EDIT_CREATE, { task })}
                            >
                                <TaskMain alignItems>
                                    <CheckMark
                                        $completed={task.completed}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            onCompeteTaskHandler(task._id)
                                        }}
                                    >
                                        {CHECK_ICON}
                                    </CheckMark>
                                    <TaskTitle>
                                        <TaskTitleInner>
                                            {parseLinksInText(task.title, styles.colors.primary600)}
                                        </TaskTitleInner>
                                    </TaskTitle>
                                </TaskMain>

                                <RightMeta>
                                    <DueDateCell>
                                        {task.end === 0 ? (
                                            <TaskDate $muted>{'No Due Date'}</TaskDate>
                                        ) : (
                                            <DueDateBlock alignItems>
                                                <TaskDate $red={moment().isAfter(moment.unix(task.end)) && !!task.end}>
                                                    {moment.unix(task.end).format('MMM D, YYYY')}
                                                </TaskDate>
                                                {task.repeating?.unit && task.repeating?.unit !== 'Never' && (
                                                    <RepeatHint>
                                                        <SvgFragment type="repeat" />
                                                        <span>{`This task repeats ${task.repeating.unit.toLowerCase()}.`}</span>
                                                    </RepeatHint>
                                                )}
                                            </DueDateBlock>
                                        )}
                                    </DueDateCell>
                                    <PriorityCell onClick={(e) => e.stopPropagation()}>
                                        {renderTags(task.priority)}
                                    </PriorityCell>
                                </RightMeta>
                            </ListRow>
                        </TaskGroup>
                    ))}
                </TaskList>
            ) : (
                <EmptyPageWrapper>
                    <SvgFragment type="templateEmptyBlue" />
                    <Text $heading4>{'No Tasks to Show'}</Text>
                    <Text $lighter $paragraph4>
                        {'Create and complete tasks, or assign them to your workspace members, set reminders.'}
                    </Text>
                </EmptyPageWrapper>
            )}
        </ListScroll>
    )
}

const divider = `1px solid ${styles.colors.black200}`

const ListScroll = styled.div`
    flex: 1;
    min-height: 0;
    overflow: hidden;
`

const TaskList = styled(Column)`
    width: 100%;
    gap: 4px;
`

const TaskGroup = styled.div<IListRowProps>`
    width: 100%;
    border-bottom: ${({ $isLast }) => ($isLast ? 'none' : divider)};
`

const ListRow = styled(Row)`
    width: 100%;
    min-height: 44px;
    padding: 12px 0;
    gap: 16px;
    cursor: pointer;
    background: ${styles.colors.white};
    border-radius: 4px;
`

const TaskMain = styled(Row)`
    flex: 1;
    min-width: 0;
    gap: 10px;
`

const TaskTitle = styled.div`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    font-family: NunitoSans, 'Nunito Sans', sans-serif;
    font-size: 14px;
    line-height: 20px;
    font-weight: 700;
    color: ${styles.colors.black600};
    word-break: break-word;
`

const TaskTitleInner = styled.span`
    display: inline;

    a {
        display: inline;
        word-break: break-all;
        color: ${styles.colors.primary600};
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }
`

const RightMeta = styled.div`
    flex-shrink: 0;
    display: grid;
    grid-template-columns: 108px 62px;
    gap: 16px;
    align-items: center;
`

const DueDateCell = styled.div`
    display: flex;
    justify-content: flex-end;
    min-width: 0;
`

const DueDateBlock = styled(Column)`
    align-items: flex-end;
`

const TaskDate = styled.span<{ $muted?: boolean; $red?: boolean }>`
    font-family: NunitoSans;
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    white-space: nowrap;
    color: ${({ $muted, $red }) =>
        $red ? styles.colors.red600 : $muted ? styles.colors.black300 : styles.colors.black600};
`

const PriorityCell = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    min-width: 0;
`

const CheckMark = styled.div<ICheckMark>`
    flex-shrink: 0;
    border-radius: 50%;
    background-color: ${({ $completed }) =>
        $completed ? styles.colors.primary600 : styles.colors.black300};
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    ${({ $completed }) => $completed && 'pointer-events: none;'}

    &:hover {
        background-color: ${styles.colors.primary600};
    }
`

const RepeatHint = styled.div`
    position: relative;
    display: flex;
    cursor: pointer;

    & span {
        display: none;
        position: absolute;
        right: 0;
        background-color: #333951;
        border-radius: 5px;
        z-index: 1;
        transform: translateY(-24px);
        padding: 0 5px;
        font-family: NunitoSans;
        font-size: 12px;
        line-height: 20px;
        color: white;
        white-space: nowrap;
    }

    &:hover span {
        display: block;
    }
`

const TaskSkeletonRow = styled(Row)<IListRowProps>`
    width: 100%;
    min-height: 44px;
    padding: 12px 0;
    gap: 16px;
    align-items: center;
    border-bottom: ${({ $isLast }) => ($isLast ? 'none' : divider)};
`

const SkeletonTaskTitle = styled.div`
    flex: 1;
    min-width: 0;
    max-width: 52%;
`

const SkeletonRightMeta = styled.div`
    flex-shrink: 0;
    display: grid;
    grid-template-columns: 108px 62px;
    gap: 16px;
    align-items: center;
    margin-left: auto;
`

const EmptyPageWrapper = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 24px 0;
    text-align: center;

    & svg {
        width: 42px;
        height: 42px;
    }
`

export default TasksTable
